// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { BottomSheet, type UseModalState } from "@clipboard-health/ui-components";
import { Skeleton, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import {
  type InterviewWithDetails,
  transformResponseIntoInterview,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useState } from "react";
import { useHistory } from "react-router-dom";

import { type PlacementCandidate } from "../../../PlacementCandidate/types";
import { useUpdateInterview, WORKER_OUTCOME_RESULT } from "../../api/useUpdateInterview";
import { useInterviewAttendanceOutcome } from "../../hooks/useInterviewAttendanceOutcome";
import { PLACEMENT_PATHS } from "../../paths";
import { FEEDBACK_STEP, type FeedbackStep } from "./feedbackStep";
import { InterviewAttendanceConfirmationContent } from "./InterviewAttendanceConfirmationContent";
import { InterviewAttendanceOptionsContent } from "./InterviewAttendanceOptionsContent";
import { ListWorkplaceInterviewFeedbackContent } from "./ListWorkplaceInterviewFeedbackContent";
import { WorkplaceInterviewFeedbackContent } from "./WorkplaceInterviewFeedbackContent";

interface InterviewAttendanceFeedbackDialogProps {
  placementCandidate: PlacementCandidate;
  modalState: UseModalState;
  onClose?: () => void;
}

export function InterviewAttendanceFeedbackDialog(props: InterviewAttendanceFeedbackDialogProps) {
  const { placementCandidate, modalState, onClose } = props;
  const worker = useDefinedWorker();
  const history = useHistory();

  const {
    isLoading: isFetchingInterviews,
    isSuccess: isGetInterviewsSuccess,
    data: interviewsDataWithPages,
    fetchNextPage: fetchNextPageInterviews,
    refetch: refetchInterviews,
    hasNextPage: hasNextPageInterview,
    isFetchingNextPage: isFetchingInterviewsNextPage,
  } = useInterviewAttendanceOutcome(
    {
      workerId: placementCandidate.worker.id,
      withDismissed: false,
    },
    { enabled: modalState.modalIsOpen }
  );
  const { mutateAsync: updateInterview } = useUpdateInterview();
  const [selectedInterview, setSelectedInterview] = useState<InterviewWithDetails>();
  const [feedbackStep, setFeedbackStep] = useState<FeedbackStep>();

  const interviews = interviewsDataWithPages?.pages.flatMap((page) =>
    page.data.map((interview) =>
      transformResponseIntoInterview(interview, page.included ?? [], worker.tmz ?? "")
    )
  );
  const activeInterview =
    selectedInterview ?? (interviews && interviews.length === 1 ? interviews[0] : undefined);
  const hasPendingInterview = interviews && interviews.length > 0;

  useLogEffect(
    APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_FEEDBACK_DIALOG_VIEWED,
    {
      placementCandidate,
      interviews,
      ...(interviews ? { interviewsCount: interviews.length } : undefined),
    },
    { enabled: isGetInterviewsSuccess }
  );

  function handleCtaClicked(toJobsPath?: boolean) {
    if (interviews && interviews.length === 1) {
      setSelectedInterview(interviews[0]);
      setFeedbackStep(FEEDBACK_STEP.workplace);
    } else if (interviews && interviews.length > 1) {
      setSelectedInterview(undefined);
      setFeedbackStep(FEEDBACK_STEP.workplaces);
    } else {
      modalState.closeModal();
      if (toJobsPath) {
        history.push(PLACEMENT_PATHS.ROOT);
      }
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  function renderContent() {
    if (isFetchingInterviews) {
      return (
        <Stack
          spacing={8}
          alignItems="center"
          justifyContent="center"
          sx={{ paddingX: 8, paddingY: 6 }}
        >
          <Stack alignItems="center" spacing={8}>
            <Skeleton variant="circular" width={98} height={98} />
            <Stack spacing={0} alignItems="center">
              <Skeleton width={256} height={64} />
              <Skeleton width={360} height={32} />
            </Stack>
          </Stack>
        </Stack>
      );
    }

    if (feedbackStep === "got-offer") {
      return (
        <InterviewAttendanceConfirmationContent
          step={feedbackStep}
          placementCandidate={placementCandidate}
          interview={activeInterview}
          title="Congratulations!"
          description="We’ll reach out soon about how you can claim your gift if you decide to move forward."
          illustration="new-jobs"
          ctaOnClick={() => {
            handleCtaClicked();
          }}
          ctaText={hasPendingInterview ? "Share next outcome" : "Done"}
        />
      );
    }

    if (feedbackStep === "not-heard-back") {
      return (
        <InterviewAttendanceConfirmationContent
          step={feedbackStep}
          placementCandidate={placementCandidate}
          interview={activeInterview}
          title="We appreciate your feedback"
          description="We’ll follow up with you in 3 days about your offer status."
          ctaText={hasPendingInterview ? "Share next outcome" : "Keep browsing jobs"}
          ctaOnClick={() => {
            handleCtaClicked(true);
          }}
        />
      );
    }

    if (feedbackStep === "not-offer-the-role" && activeInterview) {
      return (
        <InterviewAttendanceOptionsContent
          interview={activeInterview}
          step={feedbackStep}
          placementCandidate={placementCandidate}
          refetchInterviews={refetchInterviews}
          isFetchingInterviews={isFetchingInterviews}
          heading="Please share the reason you did not interview."
          options={[
            {
              label: "Schedule misalignment",
              value: WORKER_OUTCOME_RESULT.scheduleMisalignment,
            },
            {
              label: "Pay misalignment",
              value: WORKER_OUTCOME_RESULT.payMisalignment,
            },
            {
              label: "Not enough experience",
              value: WORKER_OUTCOME_RESULT.notEnoughExperience,
            },
            {
              label: "I’m not sure",
              value: WORKER_OUTCOME_RESULT.notSure,
            },
          ]}
          onOptionSelected={() => {
            setFeedbackStep("not-offer-confirmation");
          }}
        />
      );
    }

    if (feedbackStep === "not-offer-confirmation") {
      return (
        <InterviewAttendanceConfirmationContent
          step={feedbackStep}
          placementCandidate={placementCandidate}
          interview={activeInterview}
          title="We appreciate your feedback"
          description="Keep your head up! You’ll find the right match."
          ctaText={hasPendingInterview ? "Share next outcome" : "Keep browsing jobs"}
          ctaOnClick={() => {
            handleCtaClicked(true);
          }}
        />
      );
    }

    if (feedbackStep === "not-interview" && activeInterview) {
      return (
        <InterviewAttendanceOptionsContent
          interview={activeInterview}
          step={feedbackStep}
          placementCandidate={placementCandidate}
          refetchInterviews={refetchInterviews}
          isFetchingInterviews={isFetchingInterviews}
          heading="Please share the reason you did not interview."
          options={[
            {
              label: "Interviewer was not available",
              value: WORKER_OUTCOME_RESULT.interviewNotAvailable,
            },
            {
              label: "Facility is no longer hiring",
              value: WORKER_OUTCOME_RESULT.noLongerHiring,
            },
            {
              label: "I had to fill out an application first",
              value: WORKER_OUTCOME_RESULT.fillOutApplicationFirst,
            },
          ]}
          onOptionSelected={() => {
            setFeedbackStep("not-interview-confirmation");
          }}
        />
      );
    }

    if (feedbackStep === "not-interview-confirmation") {
      return (
        <InterviewAttendanceConfirmationContent
          step={feedbackStep}
          placementCandidate={placementCandidate}
          interview={activeInterview}
          title="We appreciate your feedback"
          description="We’ll reach out to the facility and ensure things go better next time."
          illustration="check"
          ctaText={hasPendingInterview ? "Share next outcome" : "Keep browsing jobs"}
          ctaOnClick={() => {
            handleCtaClicked(true);
          }}
        />
      );
    }

    if (interviews && interviews.length === 0 && isGetInterviewsSuccess) {
      return (
        <InterviewAttendanceConfirmationContent
          placementCandidate={placementCandidate}
          title="You’re all set for now"
          description="We’ll prompt you here after your next interview so you can confirm the result."
          ctaText="Keep browsing jobs"
          ctaOnClick={() => {
            modalState.closeModal();
            history.push(PLACEMENT_PATHS.ROOT);
          }}
        />
      );
    }

    if (!activeInterview || !interviews) {
      return (
        <ListWorkplaceInterviewFeedbackContent
          interviews={interviews ?? []}
          isFetchingNextPageInterviews={isFetchingInterviewsNextPage}
          placementCandidate={placementCandidate}
          fetchNextPageInterviews={fetchNextPageInterviews}
          hasNextPageInterview={hasNextPageInterview}
          onInterviewSelected={(interview) => {
            setFeedbackStep(FEEDBACK_STEP.workplace);
            setSelectedInterview(interview);
          }}
        />
      );
    }

    return (
      <WorkplaceInterviewFeedbackContent
        interview={activeInterview}
        placementCandidate={placementCandidate}
        refetchInterviews={refetchInterviews}
        isFetchingInterviews={isFetchingInterviews}
        onOptionSelected={(step) => {
          setFeedbackStep(step);
        }}
      />
    );
  }

  return (
    <BottomSheet canDismiss={false} modalState={modalState}>
      <Stack
        sx={{
          p: 5,
          py: 6,
        }}
      >
        <Stack justifyContent="space-between" direction="row-reverse">
          <IconButton
            size="small"
            iconType="close"
            variant="outlined"
            onClick={async () => {
              logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_FEEDBACK_DIALOG_ACTIONS, {
                action: "close",
                step: feedbackStep,
                placementCandidate,
                interview: activeInterview,
                interviews,
                ...(interviews ? { interviewsCount: interviews.length } : undefined),
              });
              onClose?.();
              modalState.closeModal();
              if (interviews) {
                await Promise.all(
                  interviews.map(async (interview) => {
                    await updateInterview({
                      interviewId: interview.id,
                      attributes: { workerOutcome: { dismissedAt: new Date() } },
                    });
                  })
                );
              }
            }}
          />
          {activeInterview &&
          (FEEDBACK_STEP.workplace === feedbackStep ||
            FEEDBACK_STEP.notInterview === feedbackStep ||
            FEEDBACK_STEP.notOfferTheRole === feedbackStep) ? (
            <IconButton
              size="small"
              iconType="chevron-left"
              variant="outlined"
              onClick={() => {
                logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_FEEDBACK_DIALOG_ACTIONS, {
                  action: "back",
                  step: feedbackStep,
                  placementCandidate,
                  interview: activeInterview,
                  interviews,
                });
                switch (feedbackStep) {
                  case "not-interview":
                  case "not-offer-the-role": {
                    setFeedbackStep("workplace");
                    break;
                  }

                  default: {
                    setSelectedInterview(undefined);
                  }
                }
              }}
            />
          ) : undefined}
        </Stack>
        {renderContent()}
      </Stack>
    </BottomSheet>
  );
}
